import React, { useState, useEffect, useContext, useRef } from "react";
import Swal from "sweetalert2";
import { Form, Button, Spinner, Modal, Row, Col } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import useApi from "../hooks/useApi";
import ForgotPasswordModal from "./ForgotPasswordModal";

const DrivingSchoolAuthModal = ({ show, onClose, isRegister = false }) => {
  const navigate = useNavigate();
  const [isLoginView, setIsLoginView] = useState(!isRegister);
  const [loginMethod, setLoginMethod] = useState("email");

  // Login state
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Registration state
  const [name, setName] = useState("");
  const [stateName, setStateName] = useState("");
  const [cityId, setCityId] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [regContact, setRegContact] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regShowPassword, setRegShowPassword] = useState(false);
  const [zipcode, setZipcode] = useState("");
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [regErrors, setRegErrors] = useState({});
  const [isRegLoading, setIsRegLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [mapError, setMapError] = useState("");

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // OTP State
  const [step, setStep] = useState("register"); // "register" or "otp"
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  
  const { loginUser, saveAuthToken, setViewMode } = useContext(AuthContext);
  const { publicAuth } = useApi();

  useEffect(() => {
    if (show && !isLoginView && step === "register") {
      fetchCities();
    }
  }, [show, isLoginView, step]);

  useEffect(() => {
    if (!show || isLoginView || step !== "register") return;

    const timer = setTimeout(() => {
      if (!mapInstanceRef.current) {
        initMap();
      } else {
        mapInstanceRef.current.invalidateSize();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [show, isLoginView, step]);

  useEffect(() => {
    if (show) {
      setIsLoginView(!isRegister);
      if (!isRegister) {
        resetFormFields();
      }
    }
  }, [show, isRegister]);

  // Resend OTP Timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const resetFormFields = () => {
    setEmail("");
    setPhone("");
    setPassword("");
    setShowPassword(false);
    setLoginErrors({});
    setIsLoginLoading(false);
    setLoginMethod("email");

    setName("");
    setStateName("");
    setCityId("");
    setAddress("");
    setLatitude("");
    setLongitude("");
    setRegContact("");
    setRegEmail("");
    setRegPassword("");
    setRegShowPassword(false);
    setZipcode("");
    setRegErrors({});
    setIsRegLoading(false);
    setAgreeTerms(false);
    setMapError("");

    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    // Reset OTP state
    setStep("register");
    setOtp("");
    setOtpError("");
    setResendTimer(0);
    setCanResend(false);
    setSubmissionData(null);
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await publicAuth(
        "https://nominatim.openstreetmap.org/reverse",
        "GET",
        {
          lat,
          lon: lng,
          format: "json",
          addressdetails: 1,
          "accept-language": "en"
        },
        {
          headers: {
            "User-Agent": "ASYSTA Training Academy App"
          }
        }
      );

        const displayName = response.data?.display_name;
        if (displayName) {
          setAddress(displayName);
          setRegErrors(prev => ({ ...prev, address: "" }));
        }

        const address = response.data?.address || {};
        const nextState = address.state || address.state_district || "";
        const nextCity = address.city || address.town || address.village || "";

        if (nextState) {
          setStateName(nextState);
          setRegErrors(prev => ({ ...prev, stateName: "" }));
        }

        if (nextCity && Array.isArray(cities) && cities.length) {
          const match = cities.find((c) =>
            String(c.name || "").toLowerCase() === String(nextCity).toLowerCase()
          );
          if (match) {
            setCityId(String(match.id));
            setRegErrors(prev => ({ ...prev, cityId: "" }));
            if (match.state_name && !nextState) {
              setStateName(match.state_name);
              setRegErrors(prev => ({ ...prev, stateName: "" }));
            }
          }
        }
      } catch (error) {
      console.error("Reverse geocoding failed:", error);
      setMapError("Unable to fetch address from the selected location.");
    }
  };

  const setMarkerAndLocation = async (lat, lng, { updateAddress = true, zoom = true } = {}) => {
    if (!window.L || !mapInstanceRef.current) return;

    const L = window.L;
    const nextLat = Number(lat);
    const nextLng = Number(lng);

    if (Number.isNaN(nextLat) || Number.isNaN(nextLng)) return;

    if (markerRef.current) {
      markerRef.current.setLatLng([nextLat, nextLng]);
    } else {
      markerRef.current = L.marker([nextLat, nextLng], { draggable: true }).addTo(mapInstanceRef.current);
      markerRef.current.on("dragend", (event) => {
        const pos = event.target.getLatLng();
        setMarkerAndLocation(pos.lat, pos.lng);
      });
    }

    if (zoom) {
      mapInstanceRef.current.setView([nextLat, nextLng], 16);
    }

    setLatitude(nextLat.toFixed(7));
    setLongitude(nextLng.toFixed(7));
    setRegErrors(prev => ({ ...prev, location: "" }));
    setMapError("");

    if (updateAddress) {
      await reverseGeocode(nextLat, nextLng);
    }
  };

  const initMap = () => {
    if (mapInstanceRef.current || !mapContainerRef.current) return;

    if (!window.L) {
      setMapError("Map library failed to load. Please refresh the page.");
      return;
    }

    const L = window.L;
    const map = L.map(mapContainerRef.current, {
      center: [20.5937, 78.9629],
      zoom: 5,
      zoomControl: true
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors"
    }).addTo(map);

    map.on("click", (event) => {
      const { lat, lng } = event.latlng;
      setMarkerAndLocation(lat, lng);
    });

    mapInstanceRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMarkerAndLocation(position.coords.latitude, position.coords.longitude, { zoom: true });
        },
        () => {},
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 60000
        }
      );
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMapError("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setMarkerAndLocation(position.coords.latitude, position.coords.longitude, { zoom: true });
      },
      () => {
        setMapError("Unable to access your location. Please select on the map.");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const fetchCities = async () => {
    if (cities.length > 0) return;

    setIsCitiesLoading(true);
    try {
      const response = await publicAuth(`cities`);

      if (response.data.status && Array.isArray(response.data.data)) {
        const rows = response.data.data;
        setCities(rows);
        const stateList = Array.from(
          new Set(rows.map((c) => (c.state_name || "").trim()).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        setStates(stateList);
      } else {
        throw new Error(response.data.message || "Failed to load cities");
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to load cities. Please try again later.'
      });
    } finally {
      setIsCitiesLoading(false);
    }
  };

  const validateLogin = () => {
    const newErrors = {};

    if (loginMethod === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email.trim()) newErrors.email = "Email is required";
      else if (!emailRegex.test(email)) newErrors.email = "Enter a valid email";
    } else {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phone.trim()) newErrors.phone = "Phone number is required";
      else if (!phoneRegex.test(phone)) newErrors.phone = "Enter 10-digit phone number";
    }

    if (!password.trim()) newErrors.password = "Password is required";
    else if (password.length < 6) newErrors.password = "Minimum 6 characters";

    setLoginErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRegistration = () => {
    const newErrors = {};

    if (!name.trim()) newErrors.name = "School name is required";
    if (!stateName) newErrors.stateName = "Please select a state";
    if (!cityId) newErrors.cityId = "Please select a city";
    if (!address.trim()) newErrors.address = "Address is required";
    if (!latitude || !longitude) newErrors.location = "Please select your location on the map";

    const phoneRegex = /^[0-9]{10}$/;
    if (!regContact.trim()) newErrors.regContact = "Phone number is required";
    else if (!phoneRegex.test(regContact)) newErrors.regContact = "Enter 10-digit phone number";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regEmail.trim()) newErrors.regEmail = "Email is required";
    else if (!emailRegex.test(regEmail)) newErrors.regEmail = "Enter a valid email";

    if (!regPassword.trim()) newErrors.regPassword = "Password is required";
    else if (regPassword.length < 6) newErrors.regPassword = "Minimum 6 characters";

    if (!zipcode.trim()) newErrors.zipcode = "Zipcode is required";
    else if (!/^\d{6}$/.test(zipcode)) newErrors.zipcode = "Enter 6-digit zipcode";

    if (!agreeTerms) newErrors.agreeTerms = "You must agree to the terms";

    setRegErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    setIsLoginLoading(true);
    try {
      const payload = {
        password,
        [loginMethod === "email" ? "email" : "phone"]: loginMethod === "email" ? email : phone
      };

      const response = await publicAuth(`driving-school/login`, "POST", payload);

      if (response.data.status === true && response.data.data) {
        const user = response.data.data;
        loginUser(user);
        setViewMode("school");
        saveAuthToken(response.data.token);

        navigate('/school/dashboard');
        onClose();
      } else {
        throw new Error(response.data.message || "Invalid credentials");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error.response?.data?.message || error.message || "Something went wrong.",
      });
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateRegistration()) return;

    setIsRegLoading(true);
    try {
      const payload = {
        name,
        city_id: cityId,
        address,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        contact: regContact,
        zipcode,
        email: regEmail,
        password: regPassword
      };

      const response = await publicAuth(`driving-school/register`, "POST", payload);

      if (response.data.status === true) {
        // Save submission data for OTP verification
        setSubmissionData({
          email: regEmail,
          registrationData: payload
        });
        
        // Start OTP flow
        setResendTimer(30);
        setCanResend(false);
        setStep("otp");

        Swal.fire({
          title: "OTP Sent!",
          text: "Please check your email for the OTP verification.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        throw new Error(response.data.message || "Registration failed");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: error.response?.data?.message || error.message || "Something went wrong during registration.",
      });
    } finally {
      setIsRegLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsRegLoading(true);
    try {
      const response = await publicAuth(`driving-school/verify-email-otp`, "POST", {
        email: submissionData.email,
        otp: otp
      });

      if (response.data.status === true && response.data.data) {
        await Swal.fire({
          icon: "success",
          title: "Email Verified",
          text: "Verification successful. Your account is now under Asysta team review.",
          showConfirmButton: true,
        });

        resetFormFields();
        onClose();
        setIsLoginView(true);
        setStep("register");
      } else {
        throw new Error(response.data.message || "Invalid OTP");
      }
    } catch (error) {
      setOtpError(error.response?.data?.message || error.message || "OTP verification failed");
    } finally {
      setIsRegLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!canResend) return;

    setIsRegLoading(true);
    try {
      await publicAuth(`driving-school/resend-otp`, "POST", { 
        email: submissionData.email 
      });
      
      setResendTimer(30);
      setCanResend(false);
      setOtp("");
      setOtpError("");
      
      Swal.fire({
        title: "OTP Resent!",
        text: "A new OTP has been sent to your email.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to resend OTP",
      });
    } finally {
      setIsRegLoading(false);
    }
  };

  const handleForgotClick = () => {
    setShowForgot(true);
  };

  const handleForgotClose = () => {
    setShowForgot(false);
  };

  const handleClose = () => {
    resetFormFields();
    onClose();
  };

  if (!show) return null;

  const isRegisterView = !isLoginView && step === "register";
  const modalSize = isRegisterView ? "xl" : "md";
  const modalDialogClass = isRegisterView ? "driving-school-auth-modal" : undefined;

  return (
    <>
      {/* Forgot Password Modal - Separate Layer */}
      <ForgotPasswordModal
        show={showForgot}
        handleClose={handleForgotClose}
        userType="school" // School के लिए विशेष type
      />

      {/* Driving School Auth Modal */}
      <Modal 
        show={show} 
        onHide={handleClose} 
        centered 
        size={modalSize}
        dialogClassName={modalDialogClass}
        style={showForgot ? { pointerEvents: 'none', opacity: 0.5 } : {}}
      >
        <Modal.Header 
          closeButton 
          style={showForgot ? { pointerEvents: 'none' } : {}}
        >
          <Modal.Title className="w-100 text-center">
            {step === "otp" 
              ? "VERIFY YOUR EMAIL" 
              : isLoginView 
                ? "Driving School Login" 
                : "Driving School Registration"
            }
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={showForgot ? { pointerEvents: 'none' } : {}}>
          {/* OTP Verification Form */}
          {step === "otp" && (
            <div className="text-center">
              <div className="mb-4">
                <p className="mb-2">
                  Enter the 6-digit OTP sent to
                </p>
                <p className="fw-bold text-primary">{submissionData?.email}</p>
              </div>

              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Enter OTP"
                  className="text-center"
                  style={{
                    letterSpacing: "10px",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    height: "50px"
                  }}
                  value={otp}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setOtp(val);
                    setOtpError("");
                  }}
                  maxLength={6}
                  disabled={isRegLoading || showForgot}
                  readOnly={showForgot}
                />
                {otpError && (
                  <Form.Text className="text-danger d-block mt-2">
                    {otpError}
                  </Form.Text>
                )}
              </Form.Group>

              <div className="mb-4">
                <small className="text-muted">
                  Didn't receive?{" "}
                  <span
                    className={`${!canResend || isRegLoading || showForgot ? "text-muted" : "text-primary"}`}
                    style={{
                      cursor: canResend && !isRegLoading && !showForgot ? "pointer" : "not-allowed",
                      textDecoration: canResend && !isRegLoading && !showForgot ? "underline" : "none"
                    }}
                    onClick={canResend && !isRegLoading && !showForgot ? resendOtp : undefined}
                  >
                    Resend OTP {resendTimer > 0 ? `in ${resendTimer}s` : ""}
                  </span>
                </small>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => setStep("register")}
                  disabled={isRegLoading || showForgot}
                  style={{ flex: 1 }}
                >
                  BACK
                </Button>
                <Button
                  variant="primary"
                  onClick={verifyOtp}
                  disabled={isRegLoading || otp.length !== 6 || showForgot}
                  style={{ flex: 1 }}
                >
                  {isRegLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      VERIFYING...
                    </>
                  ) : (
                    "VERIFY OTP"
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Registration Form */}
          {step === "register" && !isLoginView && (
            <Form onSubmit={handleRegister}>
              <Row className="g-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Select Location*</Form.Label>
                    <div
                      ref={mapContainerRef}
                      className="border rounded"
                      style={{ height: "420px", width: "100%" }}
                    />
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <small className="text-muted">
                        {latitude && longitude
                          ? `Lat: ${latitude}, Lng: ${longitude}`
                          : "Click on the map to set location"}
                      </small>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleUseCurrentLocation}
                        disabled={showForgot}
                      >
                        Use current location
                      </Button>
                    </div>
                    {mapError && (
                      <Form.Text className="text-danger d-block mt-1">
                        {mapError}
                      </Form.Text>
                    )}
                    {regErrors.location && (
                      <Form.Text className="text-danger d-block mt-1">
                        {regErrors.location}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          placeholder="Driving School Name*"
                          value={name}
                          onChange={(e) => {
                            if (!showForgot) {
                              setName(e.target.value);
                              setRegErrors(prev => ({ ...prev, name: "" }));
                            }
                          }}
                          isInvalid={!!regErrors.name}
                          disabled={showForgot}
                          readOnly={showForgot}
                        />
                        <Form.Control.Feedback type="invalid">
                          {regErrors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Select
                          value={stateName}
                          onChange={(e) => {
                            if (!showForgot) {
                              const next = e.target.value;
                              setStateName(next);
                              setCityId("");
                              setRegErrors(prev => ({ ...prev, stateName: "", cityId: "" }));
                            }
                          }}
                          isInvalid={!!regErrors.stateName}
                          disabled={isCitiesLoading || showForgot}
                        >
                          <option value="">Select State*</option>
                          {states.map((st) => (
                            <option key={st} value={st}>{st}</option>
                          ))}
                        </Form.Select>
                        {isCitiesLoading && (
                          <small className="text-muted">Loading states...</small>
                        )}
                        <Form.Control.Feedback type="invalid">
                          {regErrors.stateName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Select
                          value={cityId}
                          onChange={(e) => {
                            if (!showForgot) {
                              setCityId(e.target.value);
                              setRegErrors(prev => ({ ...prev, cityId: "" }));
                            }
                          }}
                          isInvalid={!!regErrors.cityId}
                          disabled={isCitiesLoading || !stateName || showForgot}
                        >
                          <option value="">Select City*</option>
                          {cities
                            .filter((city) => !stateName || city.state_name === stateName)
                            .map(city => (
                              <option key={city.id} value={city.id}>{city.name}</option>
                            ))}
                        </Form.Select>
                        {isCitiesLoading && (
                          <small className="text-muted">Loading cities...</small>
                        )}
                        <Form.Control.Feedback type="invalid">
                          {regErrors.cityId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Full Address*"
                      value={address}
                      onChange={(e) => {
                        if (!showForgot) {
                          setAddress(e.target.value);
                          setRegErrors(prev => ({ ...prev, address: "" }));
                        }
                      }}
                      isInvalid={!!regErrors.address}
                      disabled={showForgot}
                      readOnly={showForgot}
                    />
                    <Form.Control.Feedback type="invalid">
                      {regErrors.address}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          placeholder="Contact Number*"
                          maxLength={10}
                          value={regContact}
                          onChange={(e) => {
                            if (!showForgot) {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                              setRegContact(value);
                              setRegErrors(prev => ({ ...prev, regContact: "" }));
                            }
                          }}
                          isInvalid={!!regErrors.regContact}
                          disabled={showForgot}
                          readOnly={showForgot}
                        />
                        <Form.Control.Feedback type="invalid">
                          {regErrors.regContact}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="email"
                          placeholder="Email Address*"
                          value={regEmail}
                          onChange={(e) => {
                            if (!showForgot) {
                              setRegEmail(e.target.value);
                              setRegErrors(prev => ({ ...prev, regEmail: "" }));
                            }
                          }}
                          isInvalid={!!regErrors.regEmail}
                          disabled={showForgot}
                          readOnly={showForgot}
                        />
                        <Form.Control.Feedback type="invalid">
                          {regErrors.regEmail}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <div className="password-input-group position-relative">
                          <Form.Control
                            type={regShowPassword ? "text" : "password"}
                            placeholder="Password*"
                            value={regPassword}
                            onChange={(e) => {
                              if (!showForgot) {
                                setRegPassword(e.target.value);
                                setRegErrors(prev => ({ ...prev, regPassword: "" }));
                              }
                            }}
                            isInvalid={!!regErrors.regPassword}
                            disabled={showForgot}
                            readOnly={showForgot}
                          />
                          <button
                            type="button"
                            className="position-absolute end-0 top-50 translate-middle-y bg-transparent border-0 pe-3"
                            onClick={() => !showForgot && setRegShowPassword(!regShowPassword)}
                            disabled={showForgot}
                          >
                            {regShowPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {regErrors.regPassword}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          placeholder="Zipcode*"
                          maxLength={6}
                          value={zipcode}
                          onChange={(e) => {
                            if (!showForgot) {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setZipcode(value);
                              setRegErrors(prev => ({ ...prev, zipcode: "" }));
                            }
                          }}
                          isInvalid={!!regErrors.zipcode}
                          disabled={showForgot}
                          readOnly={showForgot}
                        />
                        <Form.Control.Feedback type="invalid">
                          {regErrors.zipcode}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Check
                      id="driving-school-agree-terms"
                      type="checkbox"
                      label="I agree to the Terms and Conditions"
                      checked={agreeTerms}
                      onChange={(e) => {
                        if (!showForgot) {
                          setAgreeTerms(e.target.checked);
                          setRegErrors(prev => ({ ...prev, agreeTerms: "" }));
                        }
                      }}
                      isInvalid={!!regErrors.agreeTerms}
                      disabled={showForgot}
                    />
                    <Form.Control.Feedback type="invalid">
                      {regErrors.agreeTerms}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100 mb-3"
                    disabled={isRegLoading || showForgot}
                  >
                    {isRegLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        PROCESSING...
                      </>
                    ) : (
                      "REGISTER DRIVING SCHOOL"
                    )}
                  </Button>

                  <div className="text-center">
                    <p className="mb-0">
                      Already have an account?{' '}
                      <Button
                        variant="link"
                        onClick={() => !showForgot && setIsLoginView(true)}
                        className="p-0"
                        disabled={showForgot}
                      >
                        Login here
                      </Button>
                    </p>
                  </div>
                </Col>
              </Row>
            </Form>
          )}

          {/* Login Form */}
          {step === "register" && isLoginView && (
            <>
              <div className="text-center mb-4">
                <Button
                  variant={loginMethod === "phone" ? "primary" : "outline-primary"}
                  className="me-2"
                  onClick={() => !showForgot && setLoginMethod("phone")}
                  disabled={showForgot}
                >
                  Phone Login
                </Button>
                <Button
                  variant={loginMethod === "email" ? "primary" : "outline-primary"}
                  onClick={() => !showForgot && setLoginMethod("email")}
                  disabled={showForgot}
                >
                  Email Login
                </Button>
              </div>

              <Form onSubmit={handleLogin}>
                {loginMethod === "phone" ? (
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Phone Number*"
                      value={phone}
                      onChange={(e) => {
                        if (!showForgot) {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setPhone(value);
                          setLoginErrors(prev => ({ ...prev, phone: "" }));
                        }
                      }}
                      isInvalid={!!loginErrors.phone}
                      disabled={showForgot}
                      readOnly={showForgot}
                    />
                    <Form.Control.Feedback type="invalid">
                      {loginErrors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                ) : (
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="email"
                      placeholder="Email Address*"
                      value={email}
                      onChange={(e) => {
                        if (!showForgot) {
                          setEmail(e.target.value);
                          setLoginErrors(prev => ({ ...prev, email: "" }));
                        }
                      }}
                      isInvalid={!!loginErrors.email}
                      disabled={showForgot}
                      readOnly={showForgot}
                    />
                    <Form.Control.Feedback type="invalid">
                      {loginErrors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <div className="password-input-group position-relative">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Password*"
                      value={password}
                      onChange={(e) => {
                        if (!showForgot) {
                          setPassword(e.target.value);
                          setLoginErrors(prev => ({ ...prev, password: "" }));
                        }
                      }}
                      isInvalid={!!loginErrors.password}
                      disabled={showForgot}
                      readOnly={showForgot}
                    />
                    <button
                      type="button"
                      className="position-absolute end-0 top-50 translate-middle-y bg-transparent border-0 pe-3"
                      onClick={() => !showForgot && setShowPassword(!showPassword)}
                      disabled={showForgot}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {loginErrors.password}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mb-3"
                  disabled={isLoginLoading || showForgot}
                >
                  {isLoginLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Logging in...
                    </>
                  ) : (
                    `Login with ${loginMethod === "phone" ? "Phone" : "Email"}`
                  )}
                </Button>

                <div className="text-center">
                  <Button
                    variant="link"
                    className="p-0 mt-2 text-decoration-none"
                    onClick={handleForgotClick}
                    disabled={showForgot}
                  >
                    Forgot Password?
                  </Button>
                </div>

                <div className="text-center">
                  <p className="mb-0">
                    Don't have an account?{' '}
                    <Button
                      variant="link"
                      onClick={() => !showForgot && setIsLoginView(false)}
                      className="p-0"
                      disabled={showForgot}
                    >
                      Register here
                    </Button>
                  </p>
                </div>
              </Form>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DrivingSchoolAuthModal;
